import { useEffect, useState } from "react";
import ExternalUserDetails from "./details";
import ExternalUserForm from "./form";
import { FormProvider, useForm } from "react-hook-form";
import {
  externalUserSchema,
  type ExternalUserFormData,
} from "@/schemas/users/external-user";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router";
import { useHeaderButton } from "@/contexts/header-button-context";
import { Button, Icon, useToast } from "@elaniintc/goes-design-system";
import { useCreateExternalUser } from "@/queries/users/external-users.api";
import { cn } from "@/utils/theme";
import type { AxiosError } from "axios";
import dayjs from "dayjs";

const NewExternalUser = () => {
  const methods = useForm<ExternalUserFormData>({
    mode: "onChange",
    resolver: zodResolver(externalUserSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      document_type: "DUI",
      document_number: "",
      email: "",
      phone: {
        number: "",
        calling_code: "503",
        country: "SV",
      },
      birthdate: "",
      type: "APPLICANT",
    },
  });

  const [selectedUserType, setSelectedUserType] = useState<{
    label: string;
    value: string;
  } | null>({
    label: "Solicitante",
    value: "APPLICANT",
  });

  const navigate = useNavigate();
  const { setComp } = useHeaderButton();

  const { mutateAsync, isPending } = useCreateExternalUser();
  const { showToast } = useToast();

  const onSubmit = async (data: ExternalUserFormData) => {
    try {
      let birthdateFormatted = "";
      if (data.birthdate) {
        try {
          const date = new Date(data.birthdate);
          if (!Number.isNaN(date.getTime())) {
            birthdateFormatted = dayjs(date).format("YYYY-MM-DD");
          }
        } catch (e) {
          console.error("Error parsing birthdate:", e);
        }
      }

      const dataToSend = {
        ...data,
        birthdate: birthdateFormatted,
      };
      await mutateAsync(dataToSend);
      showToast({
        title: "Usuario externo creado exitosamente",
        description: "El usuario externo ha sido creado correctamente.",
        options: {
          type: "success",
          duration: 4000,
        },
      });
      navigate("/usuarios?user-tab=externos");
    } catch (e) {
      const error = e as AxiosError<{
        message: string;
        errors?: {
          [key: string]: string[];
        };
      }>;

      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        if (errors) {
          Object.keys(errors).forEach((key) => {
            if (key === "email") {
              methods.setError("email", {
                type: "manual",
                message:
                  errors.email[0] || "El correo electrónico ya está registrado",
              });
            }
            if (key === "document_number") {
              methods.setError("document_number", {
                type: "manual",
                message:
                  errors.document_number[0] ||
                  "El documento ya está registrado",
              });
            }
          });
        }
      }

      showToast({
        title: "Error al crear el usuario externo",
        description:
          error.response?.data?.message ||
          "Revisa los datos ingresados e inténtalo de nuevo.",
        options: {
          type: "error",
          duration: 4000,
        },
      });
    }
  };

  useEffect(() => {
    setComp(
      <>
        <Button
          variant="quiet"
          size="xs"
          type="button"
          onClick={() => navigate(-1)}
        >
          Cancelar
          <Icon type="far" name="times" />
        </Button>
        <Button
          variant="default"
          size="xs"
          form="external-user-form"
          type="submit"
          disabled={!methods.formState.isValid || isPending}
        >
          Crear usuario
          <Icon
            type="far"
            name={isPending ? "spinner-third" : "plus"}
            className={cn("text-current", isPending && "animate-spin")}
          />
        </Button>
      </>,
    );
    return () => {
      setComp(null);
    };
  }, [navigate, setComp, isPending, methods.formState.isValid]);

  return (
    <div className="grid grid-cols-1 tablet:grid-cols-2 gap-3">
      <FormProvider {...methods}>
        <ExternalUserDetails selectedUserType={selectedUserType} />
        <ExternalUserForm
          setSelectedUserType={setSelectedUserType}
          onSubmit={onSubmit}
        />
      </FormProvider>
    </div>
  );
};

export default NewExternalUser;
